import { useEffect, useRef, type ReactNode } from 'react';
import { buildMetaTopics, initPreventer, startTopicAccessCheck } from '@/mqtt/preventer';
import { LOCAL_BROKER_MODE } from '@/lib/localBroker';
import { PREVIEW_MODE } from '@/lib/previewMode';
import { LocalBrokerProvider } from '@/mqtt/LocalBrokerProvider';
import { PreviewModeProvider } from '@/mqtt/PreviewModeProvider';
import { useMqttConnection } from '@/mqtt/useMqttConnection';
import { useConnectGate, useSignageStore } from '@/store/signageStore';

/**
 * The connect gate, as a React component. Replaces both legacy Vuex plugins' lifecycle
 * (`checkAndConnect` in `store/index.ts`, docs/MIGRATION.md §5).
 *
 * ```
 * mount                                    -> initPreventer()  -> setPreventerLoaded(token)
 * ModuleLoader mount (SYM-491)                                 -> setModuleLoaded()
 * widgets mount     (SYM-490 useWidgetData)                    -> registerTopic(...)
 *
 * gate: preventerLoaded && moduleLoaded && topicCount > 0
 *   -> savemeta  -> metaEnabled -> setCanConnectMqtt()  -> mqtt connect + subscribe
 * ```
 *
 * All three gate inputs are asynchronous and arrive in genuinely unpredictable order — the
 * preventer script is a third-party `async` load, `moduleLoaded` depends on render, and the
 * topic count depends on how many widgets a config happens to carry. So the gate is
 * *derived* from the three, never latched by whichever finishes last.
 *
 * ## Why refs and `getState()` rather than props and selectors
 *
 * Everything below runs outside render. The store is read imperatively so a message arriving
 * for one widget does not re-render this provider (and with it, nothing — but the habit is
 * what keeps it that way). Only the two booleans that *drive* the effects are subscribed to.
 *
 * The `children` element identity is stable across this component's re-renders, so React
 * bails out of re-rendering the widget tree when the gate flips.
 */
export function LiveMqttProvider({ children }: { children?: ReactNode }) {
  const gate = useConnectGate();

  const accessRequested = useRef(false);

  // Connect, subscribe, heartbeat and the static-config warning — shared with
  // `LocalBrokerProvider`, which differs only in where the credentials come from.
  useMqttConnection();

  /* ---- 1. token ------------------------------------------------------------------------ */

  useEffect(
    () =>
      initPreventer((token) => {
        useSignageStore.getState().setPreventerLoaded(token);
      }),
    [],
  );

  /* ---- 2. gate opens -> savemeta ------------------------------------------------------- */

  /**
   * Fires `startTopicAccessCheck` exactly once.
   *
   * Two guards, and both are needed. The dependency is the *boolean*, not the gate object,
   * so a fifth widget registering a topic after the gate opened cannot re-run this effect
   * and cancel an in-flight retry chain. The ref then makes it exactly-once even if a
   * dependency changes for some other reason — and it is reset in the cleanup, because
   * StrictMode's mount/unmount/mount cycle keeps refs alive across the remount and a guard
   * that survived it would leave the display permanently unconnected in dev.
   */
  useEffect(() => {
    if (!gate.canConnect) return;
    if (accessRequested.current) return;
    accessRequested.current = true;

    const { topicItems, baseTopic, floorCode } = useSignageStore.getState();
    const topics = buildMetaTopics({ topics: topicItems.keys(), baseTopic, floorCode });

    console.info(
      '[mqtt] gate open (preventer + module + %d topic(s)) — asking the preventer for access',
      topicItems.size,
    );

    const cancel = startTopicAccessCheck({
      token: gate.token,
      topics,
      onGranted: () => {
        useSignageStore.getState().setCanConnectMqtt();
      },
    });

    return () => {
      cancel();
      accessRequested.current = false;
    };
  }, [gate.canConnect, gate.token]);

  return <>{children}</>;
}

/**
 * `PREVIEW_MODE` and `LOCAL_BROKER_MODE` both fold to literals at build time, so a preview
 * bundle has no reachable path into the preventer or the client and a display bundle has no
 * reachable path into the static-credential provider — see `@/lib/previewMode` and
 * `@/lib/localBroker` for why the *provider* is chosen at build time even though the fleet's
 * credentials themselves arrive at runtime (`@/lib/runtimeEnv`).
 *
 * Selecting the component here, rather than branching inside one, keeps every hook
 * unconditional in all three implementations.
 *
 * Preview wins over local-broker; `LOCAL_BROKER_MODE` is already false when preview is on,
 * so the order of these two ternaries cannot decide it on its own.
 */
export const MqttProvider = PREVIEW_MODE
  ? PreviewModeProvider
  : LOCAL_BROKER_MODE
    ? LocalBrokerProvider
    : LiveMqttProvider;
